import React, { useState, useEffect, useMemo, useRef } from "react";
import { useLocation } from "react-router-dom";
import * as XLSX from "xlsx";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Box,
  Button,
  TableSortLabel,
  Grid,
  TextField,
  InputAdornment,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { TableBodySkeleton } from "../../utils/TableSkeleton";
import TableChartIcon from "@mui/icons-material/TableChart";
import MainLayout from "../MainLayout";
import { useDispatch, useSelector } from "react-redux";
import { fetchTransfer } from "../../../redux/slices/transferSlice";
import FilterBar2 from "../admin_dashboard/FilterBar2";
import { FY_YEAR_OPTIONS } from "../../utils/FilterOptions";

// Updated TABLE_HEAD_CONFIG to use created_at for sorting
const TABLE_HEAD_CONFIG = [
  { key: "sr_no", label: "Sr. No", sortable: false },
  {
    key: "old_emp_code",
    label: "Old Emp Code",
    sortable: true,
    field: "old_emp_code",
  },
  { key: "old_name", label: "Old EMP Name", sortable: true, field: "old_name" },
  {
    key: "new_emp_code",
    label: "New Emp Code",
    sortable: true,
    field: "new_emp_code",
  },
  { key: "new_name", label: "New EMP Name", sortable: true, field: "new_name" },
  { key: "date", label: "Date", sortable: true, field: "created_at" }, // Now using created_at for sorting
  { key: "time", label: "Time", sortable: false, field: "created_at" }, // Now using created_at for sorting
];

const stableSort = (array, comparator) => {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
};

const getComparator = (order, orderBy) => {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
};

const descendingComparator = (a, b, orderBy) => {
  const valA = a[orderBy] || "";
  const valB = b[orderBy] || "";

  if (valB < valA) {
    return -1;
  }
  if (valB > valA) {
    return 1;
  }
  return 0;
};

const History = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navState = location.state || {};

  const { transferData, status } = useSelector((state) => state.transfer);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortBy, setSortBy] = useState({
    sort_by: "created_at",
    order: "desc",
  });
  const [searchTerm, setSearchTerm] = useState("");
  
  const [month, setMonth] = useState(() => {
    return navState.months || [];
  });
  
  const [year, setYear] = useState(() => {
    if (navState.years?.length > 0) {
      const targetYrs = navState.years.map(String);
      return FY_YEAR_OPTIONS.filter((y) => targetYrs.includes(y.value));
    }
    const currentYrStr = String(new Date().getFullYear());
    return FY_YEAR_OPTIONS.filter((y) => y.value === currentYrStr);
  });

  useEffect(() => {
    dispatch(fetchTransfer({
      month: month?.join(',') || "",
      year: year?.map((y) => y.value)?.join(',') || "",
    }));
  }, [month, year, dispatch]);

  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      if (navState && navState.months) return;
    }

    if (year && year.length > 0) {
      const fullFYMonths = [4, 5, 6, 7, 8, 9, 10, 11, 12, 1, 2, 3];
      const currentCalendarMonth = new Date().getMonth() + 1;
      const currentMonthIndex = fullFYMonths.indexOf(currentCalendarMonth);
      const currentYrStr = String(new Date().getFullYear());

      const hasCurrentYear = year.some((y) => String(y.value) === currentYrStr);

      if (!hasCurrentYear) {
        setMonth(fullFYMonths);
      } else {
        setMonth(fullFYMonths.slice(0, currentMonthIndex + 1));
      }
    }
  }, [year]);

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const handleSort = (columnKey) => {
    const field =
      TABLE_HEAD_CONFIG.find((h) => h.key === columnKey)?.field || columnKey;
    setSortBy((prev) => {
      if (prev.sort_by === field) {
        return {
          sort_by: field,
          order: prev.order === "asc" ? "desc" : "asc",
        };
      }
      return { sort_by: field, order: "desc" };
    });
  };

  // Updated formatDateTime function to extract from created_at
  const formatDateTime = (createdAt, type) => {
    if (!createdAt) return "";
    
    try {
      const date = new Date(createdAt);
      
      if (isNaN(date.getTime())) {
        return "";
      }
      
      if (type === "date") {
        return date.toLocaleDateString("en-GB"); // DD/MM/YYYY format
      }
      if (type === "time") {
        return date.toLocaleTimeString("en-GB", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
        }); // 24-hour format
      }
    } catch (error) {
      console.error("Error formatting date:", error);
      return "";
    }
    
    return "";
  };

  const sortedAndFilteredData = useMemo(() => {
    let currentData = Array.isArray(transferData) ? [...transferData] : [];
    const lowerCaseSearchTerm = searchTerm.toLowerCase();

    if (lowerCaseSearchTerm) {
      currentData = currentData.filter(
        (item) =>
          item.old_emp_code?.toLowerCase().includes(lowerCaseSearchTerm) ||
          item.new_emp_code?.toLowerCase().includes(lowerCaseSearchTerm) ||
          item.old_name?.toLowerCase().includes(lowerCaseSearchTerm) ||
          item.new_name?.toLowerCase().includes(lowerCaseSearchTerm) ||
          // Also search in formatted date for better search experience
          formatDateTime(item.created_at, "date")?.toLowerCase().includes(lowerCaseSearchTerm) ||
          formatDateTime(item.created_at, "time")?.toLowerCase().includes(lowerCaseSearchTerm)
      );
    }

    if (sortBy.sort_by) {
      currentData = stableSort(
        currentData,
        getComparator(sortBy.order, sortBy.sort_by)
      );
    }

    return currentData;
  }, [transferData, searchTerm, sortBy]);

  const count = sortedAndFilteredData.length;

  const paginatedData = useMemo(() => {
    return sortedAndFilteredData.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    );
  }, [sortedAndFilteredData, page, rowsPerPage]);

  const handleDownloadExcel = () => {
    const excelData = sortedAndFilteredData.map((item, index) => ({
      "Sr. No": index + 1,
      "Old Emp Code": item.old_emp_code,
      "Old Name": item.old_name,
      "New Emp Code": item.new_emp_code,
      "New Name": item.new_name,
      Date: formatDateTime(item.created_at, "date"),
      Time: formatDateTime(item.created_at, "time"),
    }));

    const ws = XLSX.utils.json_to_sheet(excelData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "TransferHistory");
    XLSX.writeFile(wb, "TransferHistory.xlsx");
  };

  return (
    <MainLayout title={'Data Transfer History'}>
      <Box sx={{ p: 3 }}>

        <Box
          sx={{
            p: 3,
            bgcolor: "background.paper",
            background: "linear-gradient(to top left, #f5f8fa, #e9eef2)",
            boxShadow: "0 6px 16px rgba(0, 0, 0, 0.12)",
            mb: 3,
            border: "1px solid #e0e6ed",
          }}
        >
          <FilterBar2
            filters={[
              { id: "year", label: "Year (FY)", options: FY_YEAR_OPTIONS, selected: year },
            ]}
            onFilterChange={(filterId, nextSelected) => {
              if (filterId === "year") {
                setYear(nextSelected); 
                if (nextSelected.length === 0) setMonth([]);
              }
            }}
            selectedMonth={month}
            setSelectedMonth={setMonth}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            searchPlaceHolder="Search History"
            actions={
              <Button
                variant="contained"
                startIcon={<TableChartIcon />}
                onClick={handleDownloadExcel}
                sx={{
                  background: "linear-gradient(135deg, #10b981, #059669)",
                  color: "#fff",
                  fontWeight: 600,
                  textTransform: "none",
                  borderRadius: "12px",
                  padding: "8px 24px",
                  boxShadow: "0 4px 12px rgba(16, 185, 129, 0.25)",
                  "&:hover": {
                    background: "linear-gradient(135deg, #059669, #047857)",
                  },
                }}
              >
                Export Excel
              </Button>
            }
          />
        </Box>

        <TableContainer component={Paper} sx={{ boxShadow: 3 }}>
          <Table>
            <TableHead sx={{ background: "#04121f", color: "white" }}>
              <TableRow>
                {TABLE_HEAD_CONFIG.map((head) => (
                  <TableCell
                    key={head.key}
                    align="center"
                    sx={{
                      fontWeight: "bold",
                      color: "white",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {head.sortable ? (
                      <TableSortLabel
                        active={sortBy.sort_by === head.field}
                        direction={
                          sortBy.sort_by === head.field ? sortBy.order : "desc"
                        }
                        onClick={() => handleSort(head.key)}
                        hideSortIcon={false}
                        sx={{
                          color: "white !important",
                          cursor: "pointer",
                          "& .MuiTableSortLabel-icon": {
                            opacity: 1,
                            color: "white !important",
                          },
                        }}
                      >
                        {head.label}
                      </TableSortLabel>
                    ) : (
                      head.label
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {status === "pending" ? (
                <TableBodySkeleton cols={7} rows={10} />
              ) : paginatedData?.length > 0 ? (
                paginatedData.map((item, index) => (
                  <TableRow key={item.id} hover>
                    <TableCell align="center">
                      {page * rowsPerPage + index + 1}
                    </TableCell>
                    <TableCell align="center">{item.old_emp_code}</TableCell>
                    <TableCell align="center">{item.old_name}</TableCell>
                    <TableCell align="center">{item.new_emp_code}</TableCell>
                    <TableCell align="center">{item.new_name}</TableCell>
                    {/* Updated to use created_at for date and time display */}
                    <TableCell align="center">
                      {formatDateTime(item.created_at, "date")}
                    </TableCell>
                    <TableCell align="center">
                      {formatDateTime(item.created_at, "time")}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={TABLE_HEAD_CONFIG.length} align="center">
                    No transfer history found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={count}
          page={page}
          onPageChange={handlePageChange}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPageOptions={[5, 10, 20, 50]}
        />
      </Box>
    </MainLayout>
  );
};

export default History;